import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { GitMerge } from 'lucide-react';
import api from '../../../lib/api';
import Button from '../../../components/Button';
import AdminModal from '../../components/AdminModal';

interface DuplicateMember {
  person_id: string;
  full_name: string | null;
  candidate_row_count: number;
  candidate_latest_year: number | null;
  legislator_row_count: number;
  legislator_latest_session_year: number | null;
  contact_method_count: number;
  has_photo: boolean;
  person_quality_score: number;
}

interface DuplicateGroup {
  name_key: string;
  duplicate_count: number;
  members: DuplicateMember[];
  recommended_target_person_id: string | null;
}

const apiError = (error: any, fallback: string) =>
  error?.response?.data?.error ??
  error?.response?.data?.message ??
  error?.response?.data?.errors?.[0]?.msg ??
  fallback;

const memberSummary = (member: DuplicateMember) =>
  [
    `${member.candidate_row_count} candidac${member.candidate_row_count === 1 ? 'y' : 'ies'}` +
      (member.candidate_latest_year ? ` (latest ${member.candidate_latest_year})` : ''),
    `${member.legislator_row_count} roster row${member.legislator_row_count === 1 ? '' : 's'}` +
      (member.legislator_latest_session_year ? ` (latest ${member.legislator_latest_session_year})` : ''),
    `${member.contact_method_count} contact${member.contact_method_count === 1 ? '' : 's'}`,
    member.has_photo ? 'photo' : null,
  ]
    .filter(Boolean)
    .join(' · ');

/**
 * Duplicate-person review & merge (PEOPLE_TOOL_PLAN.md §3.3 — P3). Name
 * collisions are suggestions only; every merge is an explicit admin action
 * with a dry-run impact preview first. The confirmation renders INLINE under
 * the record being merged — never as a second stacked overlay, which would
 * paint beneath this portaled modal (both use z-[70]).
 */
const DuplicatesReviewModal: React.FC<{ isOpen: boolean; onClose: () => void }> = ({ isOpen, onClose }) => {
  const queryClient = useQueryClient();
  const [targets, setTargets] = useState<Record<string, string>>({});
  const [pendingMerge, setPendingMerge] = useState<{
    sourceId: string;
    sourceName: string;
    targetId: string;
    targetName: string;
    impact: string;
  } | null>(null);

  const handleClose = () => {
    setPendingMerge(null);
    onClose();
  };

  const suggestions = useQuery({
    queryKey: ['admin-people', 'duplicate-suggestions'],
    queryFn: async () => {
      const response = await api.get('/admin/candidates/duplicate-person-suggestions', {
        params: { limit: 100 },
      });
      return response.data.data as DuplicateGroup[];
    },
    enabled: isOpen,
  });

  const groupTarget = (group: DuplicateGroup) =>
    targets[group.name_key] ?? group.recommended_target_person_id ?? group.members[0]?.person_id ?? '';

  const dryRunMutation = useMutation({
    mutationFn: async (vars: { sourceId: string; sourceName: string; targetId: string; targetName: string }) => {
      const response = await api.post('/admin/candidates/merge-persons', {
        source_person_id: vars.sourceId,
        target_person_id: vars.targetId,
        dry_run: true,
      });
      return { vars, impact: (response.data.data?.impact_before ?? []) as Array<{ table_name: string; count: number }> };
    },
    onSuccess: ({ vars, impact }) => {
      const touched = impact
        .filter((row) => row.count > 0)
        .map((row) => `${row.table_name} (${row.count})`)
        .join(', ');
      setPendingMerge({ ...vars, impact: touched || 'no linked records' });
    },
    onError: (error: any) => toast.error(apiError(error, 'Merge preview failed')),
  });

  const mergeMutation = useMutation({
    mutationFn: () =>
      api.post('/admin/candidates/merge-persons', {
        source_person_id: pendingMerge?.sourceId,
        target_person_id: pendingMerge?.targetId,
        dry_run: false,
        reason: 'People tool duplicate review',
      }),
    onSuccess: () => {
      toast.success('Person records merged');
      setPendingMerge(null);
      // Refetch while the modal stays open — the merged duplicate (and its
      // group, once only one record remains) drops out of the list.
      queryClient.invalidateQueries({ queryKey: ['admin-people', 'duplicate-suggestions'] });
      queryClient.invalidateQueries({ queryKey: ['admin-list'] });
    },
    onError: (error: any) => toast.error(apiError(error, 'Failed to merge person records')),
  });

  return (
    <AdminModal
      isOpen={isOpen}
      title="Review Duplicate People"
      onClose={handleClose}
      footer={
        <Button variant="secondary" size="sm" onClick={handleClose}>
          Close
        </Button>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
          People sharing the same full name. Pick the record to keep (the richest record is
          preselected), then merge the others into it — every merge shows an impact preview first.
        </p>
        {suggestions.isLoading ? (
          <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary">Loading suggestions…</p>
        ) : suggestions.isError ? (
          <p className="text-sm text-error-600 dark:text-error-400">Couldn't load duplicate suggestions.</p>
        ) : (suggestions.data ?? []).length === 0 ? (
          <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary">
            No duplicate names found — nothing to review.
          </p>
        ) : (
          (suggestions.data ?? []).map((group) => {
            const targetId = groupTarget(group);
            const target = group.members.find((member) => member.person_id === targetId);
            return (
              <div key={group.name_key} className="rounded-lg border border-border-light p-3 dark:border-border-dark">
                <h4 className="mb-2 text-sm font-semibold capitalize text-text-primary dark:text-text-dark-primary">
                  {group.name_key} ({group.duplicate_count})
                </h4>
                <ul className="space-y-2">
                  {group.members.map((member) => (
                    <li key={member.person_id} className="flex flex-wrap items-center gap-2 text-sm">
                      <label className="flex flex-1 cursor-pointer items-center gap-2">
                        <input
                          type="radio"
                          name={`target-${group.name_key}`}
                          checked={member.person_id === targetId}
                          onChange={() => {
                            setPendingMerge(null);
                            setTargets((current) => ({ ...current, [group.name_key]: member.person_id }));
                          }}
                        />
                        <span>
                          <span className="font-medium text-text-primary dark:text-text-dark-primary">
                            {member.person_id === targetId ? 'Keep: ' : ''}
                            {member.full_name}
                          </span>
                          <span className="ml-1 text-xs text-text-tertiary dark:text-text-dark-tertiary">
                            {memberSummary(member)}
                          </span>
                        </span>
                      </label>
                      {member.person_id !== targetId && pendingMerge?.sourceId !== member.person_id && (
                        <Button
                          variant="outline"
                          size="sm"
                          icon={GitMerge}
                          loading={
                            dryRunMutation.isLoading &&
                            dryRunMutation.variables?.sourceId === member.person_id
                          }
                          onClick={() =>
                            dryRunMutation.mutate({
                              sourceId: member.person_id,
                              sourceName: member.full_name ?? member.person_id,
                              targetId,
                              targetName: target?.full_name ?? targetId,
                            })
                          }
                        >
                          Merge into kept
                        </Button>
                      )}
                      {pendingMerge?.sourceId === member.person_id && (
                        <div className="w-full rounded-lg border border-error-300 bg-error-50 p-3 dark:border-error-700 dark:bg-error-900 dark:bg-opacity-20">
                          <p className="text-sm text-text-primary dark:text-text-dark-primary">
                            Merge this record into &quot;{pendingMerge.targetName}&quot;? All linked
                            records move to the kept record and the duplicate is removed. Affected:{' '}
                            {pendingMerge.impact}. This cannot be undone.
                          </p>
                          <div className="mt-2 flex justify-end gap-2">
                            <Button variant="secondary" size="sm" onClick={() => setPendingMerge(null)}>
                              Cancel
                            </Button>
                            <Button
                              variant="danger"
                              size="sm"
                              icon={GitMerge}
                              loading={mergeMutation.isLoading}
                              onClick={() => mergeMutation.mutate()}
                            >
                              Merge
                            </Button>
                          </div>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })
        )}
      </div>
    </AdminModal>
  );
};

export default DuplicatesReviewModal;
